// The browser half of the Transport contract: the same UI components, backed by the catalog API
// instead of a Revit session.
//
// No progress reporting here. The WebView bridge can stream Progress frames because the host owns
// a long-running Revit operation; an HTTP request either resolves or it doesn't. Components that
// pass `onProgress` still work — the callback simply never fires — which is the right degradation
// for the two operations that report progress at all, both of which are Revit-only.

import { findRoute } from "./routeMap";
import {
  CommandUnavailableError,
  TransportError,
  type InvokeOptions,
  type Transport,
  type TransportCaps,
} from "./types";

export class HttpTransport implements Transport {
  readonly kind = "http" as const;
  readonly caps: TransportCaps = { revit: false };

  private readonly baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl.replace(/\/$/, "");
  }

  init(): Promise<void> {
    // Capabilities in the browser are static: no Revit, no year, no document units. Nothing to
    // discover, so this resolves immediately rather than burning a round-trip.
    return Promise.resolve();
  }

  async invoke<T = unknown>(command: string, payload: unknown = null, options?: InvokeOptions): Promise<T> {
    const route = findRoute(command);
    if (!route) throw new CommandUnavailableError(command, "http");

    const path = typeof route.path === "function" ? route.path(payload as never) : route.path;
    const method = typeof route.method === "function" ? route.method(payload as never) : route.method;
    const sendAsQuery = method === "GET" || method === "DELETE";

    let url = `${this.baseUrl}${path}`;
    if (sendAsQuery && payload) url += `?${toQueryString(payload)}`;

    let response: Response;
    try {
      response = await fetch(url, {
        method,
        credentials: "include",
        headers: sendAsQuery ? undefined : { "Content-Type": "application/json" },
        body: sendAsQuery || payload == null ? undefined : JSON.stringify(payload),
        signal: options?.signal,
      });
    } catch (cause) {
      // Network-level failure: no response at all. Distinguished from an error *status* below,
      // because the two need different messages ("you appear to be offline" vs the server's own).
      if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
      throw new TransportError(command, `Could not reach the catalog service (${String(cause)}).`);
    }

    if (!response.ok) {
      throw new TransportError(command, await readErrorMessage(response), response.status);
    }

    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  }
}

/**
 * Flattens a payload object into a query string. Arrays repeat the key
 * (`?year=2025&year=2026`), which is what ASP.NET Core model binding expects for collections.
 * Null and undefined members are dropped so an unset filter does not become the literal "null".
 */
function toQueryString(payload: unknown): string {
  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(payload as Record<string, unknown>)) {
    if (value == null) continue;
    if (Array.isArray(value)) {
      for (const item of value) if (item != null) params.append(key, String(item));
    } else {
      params.append(key, String(value));
    }
  }

  return params.toString();
}

/** Prefers RFC 7807 ProblemDetails (what the API emits) and degrades to the status line. */
async function readErrorMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as { detail?: string; title?: string };
    return body.detail ?? body.title ?? `Request failed with status ${response.status}.`;
  } catch {
    return `Request failed with status ${response.status}.`;
  }
}
